<template>
    <div class="bg-gray-100 pt-[72px] lg:pt-24 overflow-hidden">
        <a id="main-content" class="fixed top-0" tabindex="-1"></a>

        {{/* Breadcrumb & bg texture illustrations */}}
        <div class="container relative z-0">
            <div class="hidden lg:block">
                <Breadcrumb :items="breadcrumbItems" />
            </div>

            <div
                class="bg-illustration -top-12 lg:top-[-10rem] -left-36 lg:left-0 xl:-left-4 2xl:left-8 w-[40rem] h-[40rem]"
                v-lazy-background :lazy-background="require('~/assets/images/shapes/shape-8_yellow-400.png')"
            ></div>

            <div
                class="bg-illustration top-[30rem] lg:top-[25rem] right-0 translate-x-1/3 w-[30rem] h-[30rem] lg:w-[60rem] lg:h-[60rem]"
                style="filter: grayscale(1)"
                v-lazy-background :lazy-background="require('~/assets/images/shapes/shape-9_yellow-400.png')"
            ></div>
        </div>

        {{/* Nav bar containing the 4 program categories */}}
        <div class="relative z-2 bg-white lg:bg-transparent lg:mt-5 border-t border-gray-100 lg:border-0">
            <EventCategoryTabBar />
        </div>

        {{/* Main content */}}
        <div class="relative z-1 container">
            <div class="grid grid-cols-12 base-responsive-gap lg:mt-8 relative z-2">
                {{/* * */}}
                {{/* * */}}
                {{/* * */}}
                {{/* FILTERS */}}
                {{/* * */}}
                <div class="col-span-12 lg:col-span-4 xl:col-span-3 z-2 relative">
                    <div
                        class="filters-wrapper fixed right-0 w-full z-10 lg:relative lg:top-[unset] bg-white px-4 lg:pt-4 pb-20 lg:pb-6 lg:rounded-2xl h-full lg:h-auto overflow-auto lg:translate-x-0 transition-transform md:w-1/2 lg:w-full"
                        :class="{ 'translate-x-full': !isMobileFilterPanelVisible }"
                        :style="`top: ${stickyElementTopPosition}; max-height: calc(100vh - ${stickyElementTopPosition})`"
                    >
                        <div
                            class="flex items-center sticky top-0 left-0 justify-between py-4 lg:hidden border-b border-gray-100 bg-white z-2"
                        >
                            <span class="uppercase text-xs text-gray-600 font-semibold">
                                <T t="programs_in_varosliget.event_filters" />
                            </span>
                            <button
                                class="inline-flex items-center justify-center p-2"
                                :aria-label="$t('programs_in_varosliget.close_filters_panel')"
                                @click="hideMobileFilterPanel"
                            >
                                <i class="pi pi-times"></i>
                            </button>
                        </div>

                        <Accordion :multiple="true" :active-index="activeAccordionPanels">
                            {{/* Date filters */}}
                            <AccordionTab
                                :header="$t('programs_in_varosliget.date_filter_label')"
                                :aria-label="$t('a11y.date_filters')"
                            >
                                <div>
                                    <div>
                                        <div class="pb-4">
                                            <div
                                                v-for="option in rangeOptions"
                                                :key="`dateOption-${option.id}`"
                                                class="p-field flex items-start py-2"
                                            >
                                                <RadioButton
                                                    :id="`radio-date-${option.value}`"
                                                    v-model="selectedRange"
                                                    name="date"
                                                    :value="option.value"
                                                />
                                                <label :for="`radio-date-${option.value}`">{{ option.label }}</label>
                                            </div>

                                            <div
                                                v-if="
                                                    selectedDatepickerDateRange.length &&
                                                    !rangeOptions.some(
                                                        (range) => range.value === selectedDatepickerDateRange.join('_')
                                                    )
                                                "
                                                class="p-field flex items-start py-2"
                                            >
                                                <RadioButton
                                                    :id="`radio-date-custom`"
                                                    v-model="selectedRange"
                                                    name="date"
                                                    :value="selectedDatepickerDateRange.join('_')"
                                                />

                                                <label :for="`radio-date-custom`">
                                                    {{ customSelectedDateOptionLabelText }}
                                                </label>
                                            </div>

                                            <div class="px-2">
                                                <Button
                                                    :label="
                                                        $t(
                                                            'programs_in_varosliget.date_filter_custom_date_button_label'
                                                        )
                                                    "
                                                    class="p-button-info p-button-outlined w-full mt-4"
                                                    @click.native="handleDateFilterButtonClick"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </AccordionTab>

                            {{/* Location filter */}}
                            <AccordionTab
                                v-if="selectedCategory.id !== 4"
                                :header="$t('programs_in_varosliget.location_filter_label')"
                                :aria-label="$t('a11y.location_filters')"
                            >
                                <div class="pb-4">
                                    <div class="p-field flex items-start py-2">
                                        <Checkbox
                                            :id="`chk-type-${defaultLocationOption.value}`"
                                            v-model="selectAllLocations"
                                            binary
                                            :disabled="selectAllLocations"
                                            class="p-checkbox--always-enabled-styles"
                                        />
                                        <label :for="`chk-type-${defaultLocationOption.value}`">
                                            {{ defaultLocationOption.label }}
                                        </label>
                                    </div>

                                    <div
                                        v-for="option in locationOptions"
                                        :key="`typeOption-${option.id}`"
                                        class="p-field flex items-start py-2"
                                    >
                                        <Checkbox
                                            :id="`chk-type-${option.value}`"
                                            v-model="selectedLocations"
                                            name="location"
                                            :value="option.value"
                                        />
                                        <label :for="`chk-type-${option.value}`">{{ option.label }}</label>
                                    </div>
                                </div>
                            </AccordionTab>

                            {{/* Detail filters, for cultural events only */}}
                            <AccordionTab
                                v-if="selectedCategory.id === 1"
                                :header="$t('programs_in_varosliget.details_filter_label')"
                                :aria-label="$t('a11y.detail_filters')"
                            >
                                <div class="pb-4">
                                    <div class="p-field flex items-start py-2">
                                        <Checkbox
                                            :id="`chk-type-${defaultCulturalTypeOption.value}`"
                                            v-model="selectAllCulturalTypes"
                                            binary
                                            :disabled="selectAllCulturalTypes"
                                            class="p-checkbox--always-enabled-styles"
                                        />
                                        <label :for="`chk-type-${defaultCulturalTypeOption.value}`">
                                            {{ defaultCulturalTypeOption.label }}
                                        </label>
                                    </div>

                                    <div
                                        v-for="option in culturalTypeOptions"
                                        :key="`culturalTypeOption-${option.id}`"
                                        class="p-field flex items-start py-2"
                                    >
                                        <Checkbox
                                            :id="`chk-type-${option.value}`"
                                            v-model="selectedCulturalTypes"
                                            name="type"
                                            :value="option.value"
                                        />
                                        <label :for="`chk-type-${option.value}`">{{ option.label }}</label>
                                    </div>
                                </div>
                            </AccordionTab>
                        </Accordion>

                        {{/* Free events only filter */}}
                        <div class="p-field flex items-start py-2 mt-4">
                            <Checkbox id="free-programs-only" v-model="showFreeEventsOnly" binary />
                            <label for="free-programs-only">
                                <T t="programs_in_varosliget.free_programs_only_filter_label" />
                            </label>
                        </div>

                        {{/* Event series only filter */}}
                        <div class="p-field flex items-start py-2">
                            <Checkbox id="series-only" v-model="showEventSeriesOnly" binary />
                            <label for="series-only">
                                <T t="programs_in_varosliget.series_only_filter_label" />
                            </label>
                        </div>

                        {{/* Clear filters */}}
                        <Button
                            :label="$t('programs_in_varosliget.clear_all_filters_button_label')"
                            class="p-button-text w-full mt-6"
                            @click.native="clearFilters"
                        />
                    </div>
                </div>

                {{/* * */}}
                {{/* * */}}
                {{/* * */}}
                {{/* RESULTS */}}
                {{/* * */}}
                <div role="region" aria-live="polite" class="col-span-12 lg:col-span-8 xl:col-span-9 z-1 relative">
                    {{/* Results count */}}
                    <div class="flex items-center justify-between mb-4">
                        <div v-if="false" class="font-Poppins font-bold">
                            192 <span><T :tc="['generic.result', 192]" /></span>
                        </div>
                        <div class="flex items-center">
                            <Button
                                icon="pi pi-sliders-h"
                                icon-pos="left"
                                :label="filterCount"
                                class="lg:hidden"
                                @click.native="isMobileFilterPanelVisible = true"
                            />
                        </div>
                    </div>

                    {{/* * */}}
                    {{/* CONDITIONAL TOP CAROUSEL ROW */}}
                    {{/* * */}}
                    <div v-if="selectedCategory.id === 1 && exhibitions.length" class="mb-12">
                        {{/* Exhibitions - appears only for cultural type event results */}}
                        <h2 class="header-3 mb-8">
                            <T t="programs_in_varosliget.exhibitions" />
                        </h2>
                        <div>
                            <CardCarousel
                                :key="featuredData.type"
                                :items="exhibitions"
                                type="exhibition"
                                is-program-page-featured-carousel
                            />
                        </div>
                    </div>

                    <div v-else-if="selectedCategory.id === 2 && ligetWalks.length" class="mb-12">
                        {{/* Liget guided walks - appears only for outdoor type event results */}}
                        <h2 class="header-3 mb-8">
                            <T t="programs_in_varosliget.liget_walks" />
                        </h2>
                        <div>
                            <CardCarousel
                                :key="featuredData.type"
                                :items="ligetWalks"
                                type="exhibition"
                                is-program-page-featured-carousel
                            />
                        </div>
                    </div>

                    <div v-else-if="selectedCategory.id === 4 && sportFacilities.length" class="mb-12">
                        {{/* Sport facilities - appears only for sport type event results */}}
                        <h2 class="header-3 mb-8">
                            <T t="programs_in_varosliget.sport" />
                        </h2>
                        <div>
                            <CardCarousel
                                :key="featuredData.type"
                                :items="sportFacilities"
                                type="sport"
                                is-program-page-featured-carousel
                            />
                        </div>
                    </div>

                    {{/* * */}}
                    {{/* EVENT RESULTS */}}
                    {{/* * */}}
                    <div>
                        <div>
                            <h2
                                v-if="
                                    (selectedCategory.id === 1 && !exhibitions.length) ||
                                    (selectedCategory.id === 2 && !ligetWalks.length) ||
                                    selectedCategory.id === 3 ||
                                    (selectedCategory.id === 4 && !sportFacilities.length)
                                "
                                class="header-3 mb-8"
                            >
                                <T :t="`navigation.category_${selectedCategory.id}_programs`" />
                            </h2>
                            <h2 v-else class="header-3 mb-8">
                                <T t="programs_in_varosliget.other_events" />
                            </h2>
                        </div>

                        <ul>
                            <li v-for="(event, i) in events" :key="`event-${i}`">
                                <FocusMagnetLink v-if="i === 0" class="event-list-focus-magnet" />

                                <ProgramCard
                                    :title="event.name"
                                    :date="$getFormattedEventInterval(event)"
                                    :description="event.lead_text"
                                    :event-types="event.categories_id"
                                    :img="$getMediaImage(event.media, 'lead', 'sm', ['landscape', 'square'])"
                                    :location="$getEventLocationName(event.location_id)"
                                    :ticket-url="event.ticket_url"
                                    :registration-url="event.registration_url"
                                    :url="$getEventUrl(event)"
                                    :has-series="!!event.series"
                                    with-category-icons
                                    class="mt-4 lg:mt-7"
                                />
                            </li>
                        </ul>

                        <div v-if="!events.length && !isLoading && isInitialFetchCompleted">
                            <FocusMagnetLink
                                class="event-list-focus-magnet"
                                :aria-label="$t('programs_in_varosliget.no_results')"
                            />

                            <T t="programs_in_varosliget.no_results" />
                        </div>
                        <div v-else-if="isLoading" class="my-12 text-center">
                            <span class="loader"></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import announcement from '@/mixins/announcement';
import CardCarousel from '~/components/Carousels/CardCarousel/CardCarousel.vue';
import ProgramCard from '~/components/Cards/ProgramCard/ProgramCard.vue';
import EventCategoryTabBar from '~/components/Navigation/EventCategoryTabBar/EventCategoryTabBar.vue';

export default {
    name: 'ProgramFinder',
    components: {
        EventCategoryTabBar,
        ProgramCard,
        CardCarousel,
    },
    mixins: [announcement],
    async asyncData({ $axios, query, redirect, i18n, localePath, store }) {
        // At this point the store index has not been refreshed yet in store/index.js, and therefore after
        // a locale change the event categories are still those of the old locale.
        //
        // As a workaround, we:

        // 1. we check if the selected category in the query even exists in the list of categories fetched in the vuex store.
        // If it does, we get its ID which is the same in bot languages.
        const originalSelectedCategoryID = store.state.data.eventCategories.find(
            (category) => category.slug === query.category
        )?.id;

        // 2. we refresh the event categories in the vuex store "manually".
        await $axios.$get('/events/categories/event-categories').then((res) => {
            store.commit('data/setEventCategories', {
                data: res,
            });
        });

        // 2. we get the selectedCategory by its id from the fresh category list,
        // which now contains the categories in the correct locale.
        const selectedCategory = store.state.data.eventCategories.find(
            (category) => category.id === originalSelectedCategoryID
        );

        // 3. If the category is not found at all, we fall back to the cultural category by default.
        if (!selectedCategory) {
            return await redirect(
                307,
                localePath({
                    name: 'programs-in-varosliget',
                    query: { ...query, category: store.state.data.eventCategories[0].slug },
                })
            );
        }

        // 4. If the category is found, but we after a locale change and the category slug in the url is
        // still in the wrong language, we update the category query param with the locale version we switched to.
        if (selectedCategory.slug !== query.category) {
            return await redirect(
                localePath({
                    name: 'programs-in-varosliget',
                    query: { ...query, category: selectedCategory.slug },
                })
            );
        }

        let sportFields = [];

        await $axios
            .$get('/sport-fields')
            .then((res) => {
                sportFields = res.data;
            })
            .catch((err) => {
                console.log(err);
            });

        const breadcrumbItems = [];

        breadcrumbItems.push([i18n.t('navigation.programs'), 'programs-in-varosliget']);

        return {
            breadcrumbItems,
            selectedCategory,
            sportFacilities: sportFields,
        };
    },
    data() {
        return {
            isInitialFetchCompleted: false,
            isLoading: false,

            events: [],
            exhibitions: [],
            ligetWalks: [],

            isMobileFilterPanelVisible: false,
            activeAccordionPanels: [0],

            showFreeEventsOnly: false,
            showEventSeriesOnly: false,

            // DATE TYPE FILTER STUFF
            isCalendarModalOpen: false,

            // tempDatepickerDateRange is the model value for the datepicker exclusively.
            // The user needs to submit the date picker's form in order to assign
            // this variable's value to selectedDatepickerDateRange.
            tempDatepickerDateRange: [],

            // When tempDatepickerDateRange's value is assigned to selectedDatepickerDateRange,
            // it becomes the value for the custom date range radio input.
            selectedDatepickerDateRange: [],

            // We need to generate a label for this custom radio input too. It is generated
            // from selectedDatepickerDateRange, and depending on whether the two dates held by selectedDatepickerDateRange
            // are different or not, is either formatted as 'MMMM DD - MMMM DD' or just 'MMMM DD'.
            customSelectedDateOptionLabelText: '',

            // selectedRange holds the v-model for the date filter radio inputs.
            // Its value is either the range in the format 'YYYY-MM-DD_YYYY-MM-DD', or 'any'
            selectedRange: null,

            rangeOptions: this.$generateDateFilterOptions(),

            // LOCATION TYPE FILTER
            selectAllLocations: true,
            selectedLocations: [],
            defaultLocationOption: {
                id: 'any',
                label: this.$t('programs_in_varosliget.all_locations'),
                value: 'location-all',
            },

            // EVENT TYPE FILTER
            selectAllCulturalTypes: true,
            selectedCulturalTypes: [],
            defaultCulturalTypeOption: {
                id: 'any',
                label: this.$t('programs_in_varosliget.all_types'),
                value: 'type-all',
            },
        };
    },
    head() {
        let key;
        switch (this.selectedCategory.id) {
            case 1:
                key = 'event-category.cultural';
                break;
            case 2:
                key = 'event-category.outdoor';
                break;
            case 3:
                key = 'event-category.family';
                break;
            case 4:
                key = 'event-category.sport';
                break;
            default:
                key = 'event-category.cultural';
        }

        return {
            title: this.$getPageTitle(key),
            meta: this.$getPageMetaData(key),
        };
    },
    computed: {
        locationOptions() {
            return this.$store.getters['data/getEventLocationOptions'];
        },
        culturalTypeOptions() {
            return this.$store.getters['data/getCulturalEventTypeOptions'];
        },
        filterCount() {
            const dateFilterCount = this.selectedRange !== 'any' ? 1 : 0;
            const locationFilterCount = this.selectedLocations.length;
            const culturalFiltersCount = this.selectedCategory.id === 1 ? this.selectedCulturalTypes.length : 0;
            const freeOnlyCount = this.showFreeEventsOnly ? 1 : 0;
            const seriesOnlyCount = this.showEventSeriesOnly ? 1 : 0;

            const total =
                dateFilterCount + locationFilterCount + culturalFiltersCount + freeOnlyCount + seriesOnlyCount;

            if (!total) {
                return null;
            }

            return String(total);
        },
        // sortedLocationOptions() {
        //     return [...this.locationOptions].sort((a, b) => {
        //         if (this.selectedLocations.includes(a.value) && this.selectedLocations.includes(b.value)) {
        //             return 0;
        //         }
        //         if (this.selectedLocations.includes(a.value) && !this.selectedLocations.includes(b.value)) {
        //             return -1;
        //         }
        //         return 1;
        //     });
        // },
        // sortedCulturalTypeOptions() {
        //     return [...this.culturalTypeOptions].sort((a, b) => {
        //         if (this.selectedCulturalTypes.includes(a.value) && this.selectedCulturalTypes.includes(b.value)) {
        //             return 0;
        //         }
        //         if (this.selectedCulturalTypes.includes(a.value) && !this.selectedCulturalTypes.includes(b.value)) {
        //             return -1;
        //         }
        //         return 1;
        //     });
        // },
        // Data for the optional featured carousel above the event results.
        featuredData() {
            if (this.selectedCategory.id === 1) {
                return {
                    type: 'exhibition',
                    title: this.$t('programs_in_varosliget.exhibitions'),
                    items: this.exhibitions,
                };
            }
            if (this.selectedCategory.id === 2) {
                return {
                    type: 'outdoor',
                    title: this.$t('programs_in_varosliget.liget_walks'),
                    items: this.ligetWalks,
                };
            }
            if (this.selectedCategory.id === 4) {
                return {
                    type: 'sport',
                    title: this.$t('programs_in_varosliget.sport'),
                    items: this.sportFacilities,
                };
            }

            return null;
        },
        accordionPanelMaxHeight() {
            return this.locationOptions.length * 3 + 8 + 'rem';
        },
    },
    watch: {
        '$route.query'(value) {
            this.selectedCategory = this.$store.state.data.eventCategories.find(
                (category) => category.slug === value.category
            );

            this.updateInputValuesAccordingToQueryParameters();
            this.fetchExhibitions();
            this.fetchLigetWalks();
            this.fetchEvents();
            window.scrollTo(0, 0);
            window.dataLayer.push({
                event: 'view_event_category',
                category: value.category,
            });
        },
        selectedRange(value) {
            const updatedDateQuery = {};
            const currentRouteQuery = { ...this.$route.query };

            // If we have a selected range, a custom range option will appear in the date filter panel.
            // Create the labels for this custom range option (month & day)
            if (this.selectedDatepickerDateRange.length) {
                let ret;

                const _d1 = this.$dayjs(this.selectedDatepickerDateRange[0]).format('MMMM DD');
                const _d2 = this.$dayjs(this.selectedDatepickerDateRange[1]).format('MMMM DD');
                const _d1WithUppercaseInitial = _d1.charAt(0).toUpperCase() + _d1.slice(1);
                const _d2WithUppercaseInitial = _d2.charAt(0).toUpperCase() + _d2.slice(1);

                if (_d1 !== _d2) {
                    ret = `${_d1WithUppercaseInitial} - ${_d2WithUppercaseInitial}`;
                } else {
                    ret = _d1WithUppercaseInitial;
                }

                this.customSelectedDateOptionLabelText = ret;
            }

            // If we have a date filter set that is not 'any', update the date query parameter
            if (value && value !== 'any') {
                const _range = value.split('_');
                updatedDateQuery.from = _range[0];
                updatedDateQuery.to = _range[1];
            }

            // If current & updated from & to queries match, we have nothing more to do
            if (updatedDateQuery.from === currentRouteQuery.from && updatedDateQuery.to === currentRouteQuery.to) {
                return;
            }

            if (!value || value === 'any') {
                delete currentRouteQuery.from;
                delete currentRouteQuery.to;
            }

            this.$router.replace(
                this.localePath({
                    name: 'programs-in-varosliget',
                    query: { ...currentRouteQuery, ...updatedDateQuery },
                })
            );
        },
        // Free events filter
        // Add or remove "free" query param according to showFreeEventsOnly
        showFreeEventsOnly(value) {
            if (!!this.$route.query.free === value) {
                return;
            }

            const updatedDateQuery = {};
            const currentRouteQuery = { ...this.$route.query };

            if (value) {
                updatedDateQuery.free = 1;
            } else {
                delete currentRouteQuery.free;
            }

            this.$router.replace(
                this.localePath({
                    name: 'programs-in-varosliget',
                    query: { ...currentRouteQuery, ...updatedDateQuery },
                })
            );
        },
        // Event series filter
        // Add or remove "series" query param according to showEventSeriesOnly
        showEventSeriesOnly(value) {
            if (!!this.$route.query.series === value) {
                return;
            }

            const updatedDateQuery = {};
            const currentRouteQuery = { ...this.$route.query };

            if (value) {
                updatedDateQuery.series = 1;
            } else {
                delete currentRouteQuery.series;
            }

            this.$router.replace(
                this.localePath({
                    name: 'programs-in-varosliget',
                    query: { ...currentRouteQuery, ...updatedDateQuery },
                })
            );
        },
        // Location filter (no selected locations)
        // Remove location query parameter from the url
        selectAllLocations(value) {
            if (value) {
                this.selectedLocations = [];

                if (!this.$route.query.location) {
                    return;
                }

                const currentRouteQuery = { ...this.$route.query };
                delete currentRouteQuery.location;

                this.$router.replace(
                    this.localePath({
                        name: 'programs-in-varosliget',
                        query: { ...currentRouteQuery },
                    })
                );
            }
        },
        // Location filter (selected locations)
        // Add selected locations to location query parameter
        selectedLocations(value) {
            this.selectAllLocations = !value.length;

            if (!value.length || this.$route.query.location === value.join(',')) {
                return;
            }

            this.$router.replace(
                this.localePath({
                    name: 'programs-in-varosliget',
                    query: { ...this.$route.query, location: value.join(',') },
                })
            );
        },
        // Cultural type filter (without value)
        // Update the type parameter in the url
        // when the selectedCulturalTypes input value changes
        selectAllCulturalTypes(value) {
            if (value) {
                this.selectedCulturalTypes = [];

                if (!this.$route.query.cultural_type) {
                    return;
                }

                const currentRouteQuery = { ...this.$route.query };
                delete currentRouteQuery.cultural_type;

                this.$router.replace(
                    this.localePath({
                        name: 'programs-in-varosliget',
                        query: { ...currentRouteQuery },
                    })
                );
            }
        },
        // Cultural type filter (with value)
        // Update the culturalTypes parameter in the url
        // when the selectAllCulturalTypes input value changes
        selectedCulturalTypes(value) {
            this.selectAllCulturalTypes = !value.length;

            if (!value.length || this.$route.query.cultural_type === value.join(',')) {
                return;
            }

            this.$router.replace(
                this.localePath({
                    name: 'programs-in-varosliget',
                    query: { ...this.$route.query, cultural_type: value.join(',') },
                })
            );
        },
    },
    created() {
        this.$nuxt.$on('update-event-page-filter-date-range', (value) => {
            this.tempDatepickerDateRange = value;
        });
        this.$nuxt.$on('submit-event-page-filter-date-range', this.submitDatepickerDateRange);
    },
    beforeMount() {
        this.activeAccordionPanels[1] = !!this.$route.query.location;
        this.activeAccordionPanels[2] = !!this.$route.query.cultural_type;
    },
    mounted() {
        this.updateInputValuesAccordingToQueryParameters();
        this.fetchExhibitions();
        this.fetchLigetWalks();
        this.fetchEvents();
    },
    methods: {
        applyGenericRequestUrlQueries(url, skipRangeFilter) {
            if (!skipRangeFilter && this.selectedRange && this.selectedRange !== 'any') {
                const _range = this.selectedRange.split('_');
                url += `&start_date=${_range[0]}`;
                url += `&end_date=${_range[1]}`;
            } else {
                url += `&start_date=${this.$dayjs().format('YYYY-MM-DD')}`;
            }

            if (this.showFreeEventsOnly) {
                url += `&is_free=1`;
            }

            if (this.showEventSeriesOnly) {
                url += `&has_series=1`;
            }

            if (this.selectedCategory.id === 1 && this.selectedCulturalTypes.length) {
                const typeIDs = this.culturalTypeOptions.reduce((acc, curr) => {
                    if (this.selectedCulturalTypes.includes(curr.value)) {
                        acc.push(curr.id);
                    }
                    return acc;
                }, []);

                url += `&type_id=${typeIDs.join(',')}`;
            }

            if (this.selectedCategory.id !== 4) {
                const locationIDs = this.locationOptions.reduce((acc, curr) => {
                    if (this.selectedLocations.includes(curr.value)) {
                        acc.push(curr.id);
                    }
                    return acc;
                }, []);

                url += `&location_id=${locationIDs.join(',')}`;
            }

            return url;
        },
        async fetchExhibitions() {
            let exhibitions = [];

            await this.$axios
                .$get(this.applyGenericRequestUrlQueries('/events?category_id=1&exhibition=1'))
                .then((res) => {
                    exhibitions = res.data;
                })
                .catch((err) => {
                    console.log(err);
                });

            this.exhibitions = exhibitions;
        },
        async fetchEvents() {
            // reset events to empty array before fetching new array of events
            // for nicer transition on category change
            this.events = [];

            this.isLoading = true;
            let url = `/events?category_id=${this.selectedCategory.id}`;

            if (this.selectedCategory.id === 1) {
                url += '&exhibition=0';
            } else if (this.selectedCategory.id === 2) {
                url += '&liget_walk=0';
            }

            await this.$axios
                .$get(this.applyGenericRequestUrlQueries(url))
                .then((res) => {
                    this.events = res.data;
                })
                .catch((err) => {
                    console.log(err);
                })
                .finally(() => {
                    this.isLoading = false;
                    this.isInitialFetchCompleted = true;
                });
        },
        async fetchLigetWalks() {
            await this.$axios
                .$get(this.applyGenericRequestUrlQueries('/events?category_id=2&liget_walk=1'))
                .then((res) => {
                    this.ligetWalks = res.data;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        handleDateFilterButtonClick() {
            const rangeFrom = this.$route.query.from || null;
            const rangeTo = this.$route.query.to || null;

            this.$nuxt.$emit('open-modal', 'event-page-calendar');
            if (rangeFrom && rangeTo) {
                this.$nuxt.$emit('set-default-event-page-filter-date-range', [
                    this.$dayjs(rangeFrom).toDate(),
                    this.$dayjs(rangeTo).toDate(),
                ]);
            }
        },
        updateInputValuesAccordingToQueryParameters() {
            const showFreeOnly = this.$route.query.free === '1';
            const showSeriesOnly = this.$route.query.series === '1';
            const rangeFrom = this.$route.query.from || null;
            const rangeTo = this.$route.query.to || null;
            const selectedLocations = this.$route.query.location ? this.$route.query.location.split(',') : null;
            const selectedCulturalTypes = this.$route.query.cultural_type
                ? this.$route.query.cultural_type.split(',')
                : null;

            if (!rangeFrom || !rangeTo) {
                this.selectedRange = this.rangeOptions[0].value;
            } else {
                const formattedRange = `${rangeFrom}_${rangeTo}`;

                this.selectedRange = formattedRange;

                if (!this.rangeOptions.some((range) => range.value === formattedRange)) {
                    this.selectedDatepickerDateRange = [rangeFrom, rangeTo];
                }
            }

            if (!selectedLocations) {
                this.selectAllLocations = true;
                this.selectedLocations = [];
            } else {
                this.selectedLocations = selectedLocations.filter((x) =>
                    this.locationOptions.find((y) => y.value === x)
                );
            }

            if (!selectedCulturalTypes) {
                this.selectAllCulturalTypes = true;
                this.selectedCulturalTypes = [];
            } else {
                this.selectedCulturalTypes = selectedCulturalTypes.filter((x) =>
                    this.culturalTypeOptions.find((y) => y.value === x)
                );
            }

            this.showFreeEventsOnly = showFreeOnly;
            this.showEventSeriesOnly = showSeriesOnly;
        },
        clearFilters() {
            this.$router.replace(
                this.localePath({
                    name: 'programs-in-varosliget',
                    query: { category: this.selectedCategory.slug },
                })
            );
        },
        openCalendarModal() {
            this.isCalendarModalOpen = true;
        },
        closeCalendarModal() {
            this.isCalendarModalOpen = false;
        },
        submitDatepickerDateRange() {
            if (!this.tempDatepickerDateRange.length) {
                return;
            }

            this.selectedDatepickerDateRange[0] = this.$dayjs(this.tempDatepickerDateRange[0]).format('YYYY-MM-DD');
            this.selectedDatepickerDateRange[1] = this.$dayjs(
                this.tempDatepickerDateRange[1] || this.tempDatepickerDateRange[0]
            ).format('YYYY-MM-DD');

            this.selectedRange = `${this.selectedDatepickerDateRange[0]}_${this.selectedDatepickerDateRange[1]}`;

            this.tempDatepickerDateRange = [];

            this.closeCalendarModal();
        },
        hideMobileFilterPanel() {
            this.isMobileFilterPanelVisible = false;
        },
    },
};
</script>

<style scoped>
.accordion-header {
    @apply w-full flex justify-between items-center py-4;
}
.filters-wrapper {
    @apply lg:max-h-[unset] lg:top-0 !important;
}

.loader {
    width: 48px;
    height: 48px;
    border: 5px solid #828282;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
